/**
 * @file URL 相关的 utils
 */
import qs from 'query-string';
/**
 * 给 URL 添加 query string
 *
 * @category URL
 * @param url - 原 URL
 * @param query - 要添加的 query
 *
 * @returns 新 URL
 */
export const appendURLQuery = (url, query) => `${url}${url.indexOf('?') !== -1 ? '&' : '?'}${qs.stringify(query)}`;
/**
 * 从字符串中解析出所有带协议前缀的 URL. 支持 URL 包含中文.
 *
 * @category URL
 * @param str - 输入
 * @returns 所有匹配到的 URL
 */
export const matchURL = (str) => {
    const reg = /(http|https):\/\/[\w\-_\u4E00-\u9FA5:/]+(\.[\w\-_\u4E00-\u9FA5]+)+([\u4E00-\u9FA5\w\-.,@?^=%&:/~+#]*[\u4E00-\u9FA5\w\-@?^=%&/~+#])?/g;
    return str
        .split('http')
        .filter((_seg, index) => index !== 0)
        .map(v => { var _a; return (_a = `http${v}`.match(reg)) === null || _a === void 0 ? void 0 : _a[0]; })
        .filter(seg => !!seg);
};
/**
 * 同 query-string 的 stringify 方法. 将一个 object 转换为 URL query string
 *
 * @category URL
 * @param query - An object of URL query
 * @returns URL query string
 */
export const stringifyURLQuery = (query) => qs.stringify(query);
/**
 * 同 query-string 的 parse 方法. 将 URL query string 转换为一个 object
 *
 * @category URL
 * @param queryString - URL query string
 * @returns Parsed object
 */
export const parseURLQuery = (queryString) => qs.parse(queryString);
/**
 * 同 query-string 的 stringifyUrl 方法. 将 URL 和 query object 转换为一个完整的 URL
 *
 * @category URL
 * @param url - URL
 * @param query - Query object
 * @returns 新的 URL
 */
export const stringifyURL = (url, query) => qs.stringifyUrl({ url, query });
/**
 * 同 query-string 的 parseUrl 方法. 将 URL 转换为不带 query 的 URL 和一个 query object
 *
 * @category URL
 * @param url - 要转换的 URL
 * @returns 返回 { url, query }
 */
export const parseURL = (url) => qs.parseUrl(url);
/**
 * 同 query-string 的 pick 方法. 返回一个只保留了指定 query 字段的 URL
 *
 * @category URL
 * @param url - 要转换的 URL
 * @param keys - 要保留的 URL query 字段数组
 * @returns 新的 URL
 */
export const pickURLQuery = (url, keys) => qs.pick(url, keys);
/**
 * 同 query-string 的 exclude 方法. 返回一个剔除了指定 query 字段的 URL
 *
 * @category URL
 * @param url - 要转换的 URL
 * @param keys - 要剔除的 URL query 字段数组
 * @returns 新的 URL
 */
export const omitURLQuery = (url, keys) => qs.exclude(url, keys);
/**
 * 检查是否为 Data URL（即前缀为 data: 协议的 URL）
 *
 * @category URL
 * @param url - URL 地址
 * @returns 是否为 Data URL
 */
export const isDataURL = (url) => url.startsWith('data:');
//# sourceMappingURL=data:application/json;base64,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